import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAV_LINKS, PRODUCT_MENU, COMPANY } from "@/lib/site";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const darkHero = pathname.startsWith("/produk/") && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        darkHero
          ? "bg-animax-black/55 backdrop-blur-xl border-b border-white/10 py-3"
          : scrolled
            ? "glass-light py-3"
            : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-4 group shrink-0 mr-6">
          <img
            src={COMPANY.logos.aplTransparent}
            alt="PT Animax Putra Lancar"
            className={`object-contain transition-all duration-300 ${
              darkHero ? "h-14 w-14 rounded-xl bg-white p-1 shadow-lg shadow-black/20" : "h-16 w-16"
            }`}
          />
          <div className="leading-tight hidden md:block">
            <div
              className={`font-display font-semibold text-xl lg:text-2xl tracking-tight transition-colors ${
                darkHero ? "text-white" : "text-animax-black"
              }`}
            >
              PT. Animax Putra Lancar
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((link) =>
            link.href.includes("#produk") ? (
              <div key={link.href} className="relative group">
                <a
                  href={link.href}
                  data-testid="nav-link-produk"
                  className={`inline-flex items-center gap-1 text-sm transition-colors ${
                    darkHero ? "text-white/75 hover:text-white" : "text-neutral-700 hover:text-animax-black"
                  }`}
                >
                  {link.label}
                  <ChevronDown className="w-3.5 h-3.5 group-hover:rotate-180 transition-transform" />
                </a>
                <div
                  data-testid="nav-product-dropdown"
                  className="absolute left-0 top-full pt-3 hidden group-hover:block"
                >
                  <div className="w-56 rounded-xl glass-light border border-neutral-200 shadow-xl p-2">
                    {PRODUCT_MENU.map((p, i) =>
                      p.available ? (
                        <Link
                          key={i}
                          to={p.href}
                          data-testid={`nav-product-item-${i}`}
                          className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-animax-black hover:bg-animax-yellow/20 transition-colors"
                        >
                          {p.label}
                          <span className="text-[10px] tracking-widest uppercase font-medium text-animax-orange">Tersedia</span>
                        </Link>
                      ) : (
                        <div
                          key={i}
                          data-testid={`nav-product-item-${i}`}
                          className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-neutral-400 cursor-default"
                        >
                          {p.label}
                          <span className="text-[10px] tracking-widest uppercase font-medium text-neutral-400">Segera</span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-link-${link.href.replace(/[\/#]/g, "")}`}
                className={`text-sm transition-colors relative group ${
                  darkHero ? "text-white/75 hover:text-white" : "text-neutral-700 hover:text-animax-black"
                }`}
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-animax-orange group-hover:w-full transition-all duration-300" />
              </a>
            )
          )}
        </nav>

        <div className="flex items-center gap-3 shrink-0 ml-6">
          <a
            href="#tim-sales"
            data-testid="nav-cta-sales"
            className={`hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
              darkHero
                ? "bg-white text-animax-black hover:bg-animax-yellow"
                : "bg-animax-black text-white hover:bg-animax-charcoal"
            }`}
          >
            Hubungi Kami
          </a>
          <button
            data-testid="nav-mobile-toggle"
            className={`lg:hidden p-2 rounded-md transition-colors ${
              darkHero ? "text-white hover:bg-white/10" : "hover:bg-neutral-100"
            }`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden mt-3 mx-6 rounded-2xl glass-light p-6 space-y-3" data-testid="mobile-menu">
          {NAV_LINKS.map((link) => (
            <div key={link.href} className="border-b border-neutral-200 last:border-0">
              <a
                href={link.href}
                onClick={() => setOpen(false)}
                className="block text-sm text-neutral-700 hover:text-animax-orange py-2"
                data-testid={`mobile-nav-link-${link.href.replace(/[\/#]/g, "")}`}
              >
                {link.label}
              </a>
              {link.href.includes("#produk") && (
                <div className="pl-4 pb-2 space-y-1">
                  {PRODUCT_MENU.map((p, i) =>
                    p.available ? (
                      <Link key={i} to={p.href} onClick={() => setOpen(false)}
                        className="block text-xs text-neutral-600 hover:text-animax-orange py-1">
                        {p.label} · Tersedia
                      </Link>
                    ) : (
                      <div key={i} className="text-xs text-neutral-400 py-1">{p.label} · Segera</div>
                    )
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </header>
  );
};
